<template>
  <div class="observation-card">
    <div class="card-header">
      <div class="header-left">
        <div class="observation-type" :class="`type-${observation.type}`">
          <span class="type-icon">{{ typeIcon }}</span>
          <span class="type-label">{{ typeLabel }}</span>
        </div>
        <div v-if="observation.duration" class="observation-duration">
          ⏱️ {{ observation.duration }}
        </div>
      </div>
      <div class="observation-date">{{ formattedDate }}</div>
    </div>

    <div class="card-body">
      <!-- Competences -->
      <div class="competences-container" v-if="observation.competences && observation.competences.length > 0">
        <div 
          v-for="(comp, index) in observation.competences" 
          :key="index"
          class="competence-tag"
        >
          <span class="tag-icon">✨</span>
          <span class="tag-text">{{ getCompetenceName(comp) }}</span>
          <span v-if="observation.competenceRatings && observation.competenceRatings[comp]" class="tag-rating">
            ({{ observation.competenceRatings[comp] }}/5)
          </span>
        </div>
      </div>
      <div class="competence-tag" v-else-if="observation.competence">
        <span class="tag-icon">✨</span>
        {{ observation.competence }}
      </div>

      <!-- Situation -->
      <p class="situation-text">{{ observation.situation || observation.factualDescription }}</p>

      <!-- Important Alerts (Decompensation) -->
      <div v-if="hasDecompensation" class="alert-box">
        <span class="alert-icon">⚠️</span>
        Signes de décompensation observés
      </div>

      <!-- Structured Info -->
      <div class="observation-details-grid">
        <!-- Context -->
        <div class="detail-group">
          <span class="group-label">Contexte</span>
          <div class="group-items">
            <span class="meta-item" title="Lieu">🌍 {{ observation.environment }}</span>
            <span v-if="socialSphere" class="meta-item" title="Sphère">🏙️ {{ socialSphere }}</span>
          </div>
        </div>

        <!-- Interventions -->
        <div class="detail-group" v-if="interventionTypes">
          <span class="group-label">Interventions</span>
          <div class="group-items">
            <span class="meta-item highlight">🤝 {{ interventionTypes }}</span>
          </div>
        </div>

        <!-- States -->
        <div class="detail-group">
          <span class="group-label">État (Avant → Après)</span>
          <div class="group-items">
            <span class="meta-item" title="État émotionnel">
              {{ emotionIcon }} {{ observation.emotionalState }} 
              <span v-if="afterEmotion" class="state-arrow">➜</span> 
              <span v-if="afterEmotion">{{ afterEmotionIcon }} {{ afterEmotion }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="card-footer">
      <div class="observer-info">
        <span class="observer-icon">👤</span>
        <span class="observer-name">{{ observation.observedBy }}</span>
        <span v-if="observation.peoplePresent?.length > 0" class="people-info">
          • Avec {{ peopleText }}
        </span>
      </div>
      <div v-if="showActions" class="card-actions">
        <button class="btn-icon-action delete" @click.stop="$emit('delete', observation)" title="Supprimer">
          <span class="btn-action-icon">🗑️</span>
          <span class="btn-action-text">Supprimer</span>
        </button>
        <button class="btn-icon-action edit" @click.stop="$emit('edit', observation)" title="Modifier">
          <span class="btn-action-icon">✏️</span>
          <span class="btn-action-text">Modifier</span>
        </button>
        <button class="btn-more" @click="$emit('view-details', observation)">
          Détails →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ObservationCard',
  props: {
    observation: {
      type: Object,
      required: true
    },
    showActions: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    typeIcon() {
      return this.observation.type === 'ciblée' ? '🎯' : '📝';
    },
    typeLabel() {
      return this.observation.type === 'ciblée' ? 'Observation ciblée' : 'Observation libre';
    },
    formattedDate() {
      const date = new Date(this.observation.date);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    },
    emotionIcon() {
      return this.getEmotionIcon(this.observation.emotionalState);
    },
    afterEmotion() {
      return this.observation.fullData?.youngAfter?.emotionalState || null;
    },
    afterEmotionIcon() {
      return this.getEmotionIcon(this.afterEmotion);
    },
    peopleText() {
      const people = this.observation.peoplePresent;
      if (!people || people.length === 0) return '';
      if (people.length === 1) return people[0];
      if (people.length === 2) return people.join(' et ');
      return `${people[0]} et ${people.length - 1} autre${people.length > 2 ? 's' : ''}`;
    },
    socialSphere() {
      const sphere = this.observation.fullData?.environment?.socialSphere;
      if (!sphere) return null;
      return sphere === 'privee' ? 'Sphère Privée' : 'Sphère Publique';
    },
    interventionTypes() {
      const types = this.observation.fullData?.interventions?.types;
      if (!types || types.length === 0) return null;
      
      const labels = {
        'guidage_verbal': 'Verbale',
        'guidage_physique': 'Physique',
        'encouragement': 'Encouragement',
        'aucune': 'Aucune'
      };
      
      return types.map(t => labels[t] || t).join(', ');
    },
    hasDecompensation() {
      const signs = this.observation.fullData?.youngAfter?.decompensationSigns;
      return signs && signs.length > 0;
    }
  },
  methods: {
    getCompetenceName(key) {
      const names = {
        'com_claire': 'Communication claire',
        'ecoute': 'Écoute',
        'collaboration': 'Collaboration',
        'gestion_interactions': 'Interactions',
        'planification': 'Planification',
        'rigueur': 'Rigueur',
        'execution': 'Exécution',
        'verification': 'Vérification',
        'autonomie': 'Autonomie',
        'demande_aide': 'Demande d\'aide',
        'initiative': 'Initiative',
        'decision': 'Décision',
        'gestion_changement': 'Adaptation',
        'flexibilite': 'Flexibilité',
        'perseverance': 'Persévérance',
        'motivation': 'Motivation',
        'gestion_stress': 'Gestion du stress',
        'gestion_emotionnelle': 'Gestion émotions',
        'prise_recul': 'Recul',
        'reactivite': 'Réactivité',
        'curiosite': 'Curiosité',
        'apprentissage': 'Apprentissage',
        'ouverture': 'Ouverture'
      };
      return names[key] || key;
    },
    getEmotionIcon(state) {
      if (!state) return '😐';
      const icons = {
        'Calme': '😌', 'calme': '😌',
        'Joyeux': '😊', 'joyeux': '😊', 'positif': '😊',
        'Concentré': '🧐', 'concentre': '🧐',
        'Stressé': '😰', 'stresse': '😰', 'negatif': '😰',
        'Triste': '😢', 'triste': '😢',
        'Fier': '😎', 'fier': '😎',
        'Frustré': '😤', 'frustre': '😤',
        'Fatigué': '😴', 'fatigue': '😴',
        'Soulagé': '😅', 'soulage': '😅',
        'Neutre': '😐', 'neutre': '😐',
        'Je ne sais pas': '❓', 'unknown': '❓'
      };
      return icons[state] || '😐';
    }
  }
};
</script>

<style scoped>
.observation-card {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  border-left: 4px solid #FF9800;
}

.observation-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.observation-type {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
}

.type-ciblée {
  background: #FFEDD5;
  color: #B85C00;
}

.type-libre {
  background: #FFF5F0;
  color: #CC6600;
}

.type-icon {
  font-size: 1rem;
}

.observation-date {
  font-size: 0.75rem;
  color: #9CA3AF;
  font-weight: 500;
}

.card-body {
  margin-bottom: 1rem;
}

.competences-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.competence-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: #FEF3C7;
  color: #92400E;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
}

.tag-icon {
  font-size: 0.875rem;
}

.situation-text {
  color: #374151;
  line-height: 1.6;
  margin: 0 0 1rem 0;
  font-size: 0.875rem;
}

.observation-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  background: #F9FAFB;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #6B7280;
}

.meta-icon {
  font-size: 1rem;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #E5E7EB;
}

.observer-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #6B7280;
}

.observer-icon {
  font-size: 1rem;
}

.observer-name {
  font-weight: 500;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.btn-icon-action {
  background: transparent;
  border: none;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.375rem 0.625rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #6B7280;
}

.btn-icon-action:hover {
  background: #F3F4F6;
  transform: translateY(-1px);
}

.btn-icon-action.delete:hover {
  background: #FEE2E2;
  color: #B91C1C;
}

.btn-icon-action.edit:hover {
  background: #E0F2FE;
  color: #0369A1;
}

.btn-action-icon {
  font-size: 1rem;
}

.btn-more {
  background: transparent;
  color: #FF9800;
  border: none;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.btn-more:hover {
  background: #FFF5F0;
}

/* New Styles */
.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.observation-duration {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6B7280;
  background: #F3F4F6;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
}

.tag-text {
  display: inline-block;
}

.tag-rating {
  font-weight: 700;
  color: #B45309;
  margin-left: 0.25rem;
}

.alert-box {
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: #B91C1C;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.alert-icon {
  font-size: 1rem;
}

.observation-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  background: #F9FAFB;
  padding: 0.75rem;
  border-radius: 8px;
}

.detail-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.group-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  color: #9CA3AF;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.group-items {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.meta-item.highlight {
  color: #B45309;
  font-weight: 600;
}

.state-arrow {
  color: #9CA3AF;
  font-size: 0.7rem;
  margin: 0 0.25rem;
}

.people-info {
  color: #9CA3AF;
}

/* Responsive styles pour mobile */
@media (max-width: 768px) {
  .observation-card {
    padding: 1rem;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .header-left {
    width: 100%;
    flex-wrap: wrap;
  }

  .observation-type {
    font-size: 0.6875rem;
    padding: 0.3rem 0.625rem;
  }

  .observation-duration {
    font-size: 0.6875rem;
    padding: 0.3rem 0.625rem;
  }

  .observation-date {
    font-size: 0.6875rem;
    width: 100%;
  }

  .competence-tag {
    font-size: 0.6875rem;
    padding: 0.3rem 0.625rem;
  }

  .situation-text {
    font-size: 0.8125rem;
  }

  .observation-details-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    padding: 0.625rem;
  }

  .card-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .observer-info {
    width: 100%;
    flex-wrap: wrap;
  }

  .card-actions {
    width: 100%;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .btn-icon-action .btn-action-text {
    display: none;
  }

  .btn-icon-action {
    padding: 0.5rem;
  }

  .btn-action-icon {
    font-size: 1.125rem;
  }

  .btn-more {
    padding: 0.5rem 1rem;
    font-size: 0.6875rem;
  }
}

@media (max-width: 640px) {
  .observation-card {
    padding: 0.875rem;
    border-left-width: 3px;
  }

  .type-icon {
    font-size: 0.875rem;
  }

  .tag-icon {
    font-size: 0.75rem;
  }

  .competences-container {
    gap: 0.375rem;
  }

  .competence-tag {
    font-size: 0.625rem;
    padding: 0.25rem 0.5rem;
  }

  .tag-rating {
    font-size: 0.625rem;
  }

  .situation-text {
    font-size: 0.75rem;
    line-height: 1.5;
  }

  .observation-details-grid {
    padding: 0.5rem;
    gap: 0.5rem;
  }

  .group-label {
    font-size: 0.625rem;
  }

  .meta-item {
    font-size: 0.6875rem;
    padding: 0.3rem 0.5rem;
  }

  .observer-info {
    font-size: 0.6875rem;
  }

  .observer-icon {
    font-size: 0.875rem;
  }

  .card-actions {
    gap: 0.375rem;
  }

  .btn-icon-action {
    padding: 0.4rem;
  }

  .btn-action-icon {
    font-size: 1rem;
  }

  .btn-more {
    padding: 0.4rem 0.875rem;
    font-size: 0.625rem;
  }

  .alert-box {
    font-size: 0.6875rem;
    padding: 0.4rem 0.625rem;
  }

  .alert-icon {
    font-size: 0.875rem;
  }
}
</style>
